import { useEffect, useState, useMemo } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "../lib/api";
import CarCard from "../components/CarCard";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";

const BODY_TYPES = ["Sedan", "SUV", "Hatchback", "Pickup", "Van"];
const TRANSMISSIONS = ["Automatic", "Manual"];
const LOCATIONS = ["Phnom Penh", "Siem Reap", "Sihanoukville", "Battambang"];

export default function Rent() {
  const { t } = useI18n();
  const [filters, setFilters] = useState({ body_type: "", transmission: "", location: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchListings = useMemo(() => async () => {
    setLoading(true);
    const cleaned = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try {
      const { data } = await api.get("/listings", {
        params: { listing_type: "rental", limit: 60, ...cleaned },
      });
      let list = data.items || [];
      if (search) {
        const s = search.toLowerCase();
        list = list.filter(l => `${l.brand} ${l.model} ${l.location}`.toLowerCase().includes(s));
      }
      setItems(list);
    } finally { setLoading(false); }
  }, [filters, search]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  return (
    <div data-testid="rent-page" className="bg-slate-50 min-h-screen">
      <Seo title={t("rent.title")} description={t("rent.subtitle")} />
      <div className="sticky top-16 z-30 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2.5">
            <Search className="w-4 h-4 text-slate-400" />
            <input data-testid="rent-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("rent.search.placeholder")} className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          <button data-testid="rent-toggle-filters" onClick={() => setShowFilters((v) => !v)} className="px-4 py-2.5 rounded-xl bg-cm-primary text-white text-sm font-medium flex items-center gap-2 press-scale">
            <SlidersHorizontal className="w-4 h-4" /> {t("buy.filters")}
          </button>
        </div>
        {showFilters && (
          <div className="border-t border-slate-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Filter testid="rent-filter-body" label={t("filter.body")} value={filters.body_type} onChange={(v) => setFilters({ ...filters, body_type: v })} options={BODY_TYPES} />
              <Filter testid="rent-filter-transmission" label={t("filter.transmission")} value={filters.transmission} onChange={(v) => setFilters({ ...filters, transmission: v })} options={TRANSMISSIONS} />
              <Filter testid="rent-filter-location" label={t("filter.location")} value={filters.location} onChange={(v) => setFilters({ ...filters, location: v })} options={LOCATIONS} />
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 py-6">
        <div className="flex items-baseline justify-between mb-4 px-4 sm:px-0">
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">{t("rent.title")}</h1>
            <p className="text-sm text-slate-500 mt-1">{t("rent.subtitle")}</p>
          </div>
          <span data-testid="rent-result-count" className="text-sm text-slate-500">{items.length} {t("rent.cars")}</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 px-4 sm:px-0">
            {Array.from({ length: 6 }).map((_, i) => <div key={`sk-${i}`} className="aspect-[16/13] bg-slate-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="rent-empty" className="text-center py-20 text-slate-500">{t("rent.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 stagger">
            {items.map((l) => <CarCard key={l.id} listing={l} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Filter({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:bg-white focus:border-cm-accent outline-none">
        <option value="">Any</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
